import React, { useEffect, useState } from 'react';
import Button from 'react-bootstrap/Button';
import ButtonGroup from 'react-bootstrap/ButtonGroup';
import Col from 'react-bootstrap/Col';
import Modal from 'react-bootstrap/Modal';
import Row from 'react-bootstrap/Row';
import { ContestTriggerV1Panel } from './ContestTriggerV1Panel';
import { ContestTriggerV2Panel } from './ContestTriggerV2Panel';
import type { ContestTriggerFormValues } from './contestTriggerShared';
import {
  isContestTriggerPopulated,
} from './contestTriggerShared';
import type { ContestTriggerV2FormValues } from './contestTriggerV2Types';
import { isContestTriggerV2Populated } from './contestTriggerV2Types';

export * from './contestTriggerShared';
export type { ContestTriggerV2FormValues } from './contestTriggerV2Types';

export type ContestTriggerActiveSchema = 'v1' | 'v2';

interface ContestTriggerSectionProps {
  valuesV1: ContestTriggerFormValues
  errorsV1: Partial<Record<keyof ContestTriggerFormValues, string>> | undefined
  touchedV1: Partial<Record<keyof ContestTriggerFormValues, boolean>> | undefined
  valuesV2: ContestTriggerV2FormValues
  errorsV2: unknown
  touchedV2: unknown
  activeSchema: ContestTriggerActiveSchema
  onActiveSchemaChange: (schema: ContestTriggerActiveSchema) => void
  onChange: (event: React.ChangeEvent) => void
  setFieldValue: (field: string, value: unknown, shouldValidate?: boolean) => void
  onRewardAmountChangeV1: (value: number) => void
  onRemoveContestTrigger: () => void
}

export function ContestTriggerSection(props: ContestTriggerSectionProps) {
  const {
    valuesV1,
    errorsV1,
    touchedV1,
    valuesV2,
    errorsV2,
    touchedV2,
    activeSchema,
    onActiveSchemaChange,
    onChange,
    setFieldValue,
    onRewardAmountChangeV1,
    onRemoveContestTrigger,
  } = props;

  const [sectionExpanded, setSectionExpanded] = useState(
    () => isContestTriggerPopulated(valuesV1) || isContestTriggerV2Populated(valuesV2),
  );
  const [removeConfirmOpen, setRemoveConfirmOpen] = useState(false);

  useEffect(() => {
    if (isContestTriggerPopulated(valuesV1) || isContestTriggerV2Populated(valuesV2)) {
      setSectionExpanded(true);
    }
  }, [valuesV1, valuesV2]);

  const handleRemoveConfirm = () => {
    onRemoveContestTrigger();
    setSectionExpanded(false);
    setRemoveConfirmOpen(false);
  };

  return (
    <>
      <Modal
        show={removeConfirmOpen}
        onHide={() => setRemoveConfirmOpen(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Remove Contest Trigger?</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          This will clear all Contest Trigger fields on this form. Remember to save the Event to commit the change.
        </Modal.Body>
        <Modal.Footer>
          <Button
            type="button"
            variant="secondary"
            onClick={() => setRemoveConfirmOpen(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="danger"
            onClick={handleRemoveConfirm}
          >
            Remove
          </Button>
        </Modal.Footer>
      </Modal>
      <div
        className="mt-5 pt-4 border-top border-secondary"
        style={{ borderTopWidth: '3px' }}
      />
      {sectionExpanded ? (
        <div className="d-flex justify-content-between align-items-center flex-wrap gap-2 mb-3 mt-2">
          <h5 className="mb-0 text-dark font-weight-bold">
            Contest Trigger
          </h5>
          <Button
            type="button"
            variant={null as any}
            className="text-white border-0"
            style={{ backgroundColor: '#888888' }}
            onClick={() => setRemoveConfirmOpen(true)}
          >
            Remove Contest Trigger
          </Button>
        </div>
      ) : (
        <h5 className="mb-3 mt-2 text-dark font-weight-bold">
          Contest Trigger
        </h5>
      )}
      {!sectionExpanded && (
        <Row className="mb-3">
          <Col>
            <Button
              type="button"
              onClick={() => setSectionExpanded(true)}
            >
              Add Contest Trigger
            </Button>
          </Col>
        </Row>
      )}
      {sectionExpanded && (
        <>
          <Row className="mb-3">
            <Col>
              <ButtonGroup aria-label="Contest trigger schema version">
                <Button
                  type="button"
                  variant={activeSchema === 'v1' ? 'primary' : 'outline-primary'}
                  onClick={() => onActiveSchemaChange('v1')}
                >
                  V1
                </Button>
                <Button
                  type="button"
                  variant={activeSchema === 'v2' ? 'primary' : 'outline-primary'}
                  onClick={() => onActiveSchemaChange('v2')}
                >
                  V2
                </Button>
              </ButtonGroup>
            </Col>
          </Row>
          {activeSchema === 'v1' && (
            <ContestTriggerV1Panel
              values={valuesV1}
              errors={errorsV1}
              touched={touchedV1}
              onChange={onChange}
              onRewardAmountChange={onRewardAmountChangeV1}
            />
          )}
          {activeSchema === 'v2' && (
            <ContestTriggerV2Panel
              values={valuesV2}
              errors={errorsV2}
              touched={touchedV2}
              onChange={onChange}
              setFieldValue={setFieldValue}
            />
          )}
        </>
      )}
    </>
  );
}
